/**
 * Video send for the private message composer: pick, encode, upload, then hand the clip id to message-sender.js.
 * The composer offers video only when the server renders #video_preview_container (pm/index.twig).
 * photo-upload.js routes a clip from the shared picker to window.videoSend.pick.
 */

import { t } from '../modules/i18n.js';
import { createUuid } from '../modules/create-uuid.js';
import { supportsVideoEncode } from '../modules/video-encode-support.js';
import { PHONE_VERIFICATION_REQUIRED, followWebPhoneGateRefusal } from '../modules/phone-gate-refusal.js';
import {
  VideoUploadError,
  cancelVideo,
  createVideoSlot,
  markVideoUploaded,
  putUploadTarget,
} from '../modules/video-upload-core.js';

/** Share of the progress bar the encode fills. The upload fills the rest. */
const ENCODE_SHARE = 0.6;

/** Error codes the send endpoint and /v1/video answer with, mapped to composer copy. */
const ERROR_KEYS = {
  video_daily_limit: 'common.pm_video_error_daily_limit',
  video_not_enabled: 'common.pm_video_error_not_enabled',
  video_sender_not_trusted: 'common.pm_video_error_sender_not_trusted',
  phone_verification_required: 'common.pm_video_error_phone_verification',
  video_requires_mutual_thread: 'common.pm_video_error_requires_mutual_thread',
  video_unavailable: 'common.pm_video_error_unavailable',
  video_busy: 'common.pm_video_error_busy',
  invalid_state: 'common.pm_video_error_invalid_state',
  too_long: 'common.pm_video_too_long',
  too_large: 'common.pm_video_too_large',
  unreadable: 'common.pm_video_unreadable',
  unsupported: 'common.pm_video_not_supported',
  unsupported_audio: 'common.pm_video_unsupported_audio',
};

/** Refusals a retry of the same clip cannot change. The user removes the clip instead. */
const FINAL_CODES = new Set([
  'video_daily_limit',
  'video_not_enabled',
  'video_sender_not_trusted',
  'phone_verification_required',
  'invalid_state',
  'too_long',
  'too_large',
  'unreadable',
  'unsupported',
  'unsupported_audio',
]);

/**
 * @param {string|undefined} code
 * @param {string} [fallback]
 * @returns {string}
 */
function videoErrorText(code, fallback) {
  const key = code ? ERROR_KEYS[code] : undefined;
  if (key) {
    return t(key);
  }
  return fallback || t('common.pm_video_failed');
}

/** The encoder chunk carries mediabunny, so it loads on the first pick only. */
function loadEncoder() {
  return import(/* webpackChunkName: "video-encode" */ '../modules/video-encode.js');
}

/**
 * One picked clip, from the file to an uploaded clip id. A retry resumes at the failed step
 * and keeps the client key, so the server returns the same slot.
 */
class MessageVideoDraft {
  /**
   * @param {Blob} file
   * @param {{
   *   encode: function(Blob, Object): Promise<{video: Blob, poster: Blob, durationMs: number}>,
   *   createSlot: typeof createVideoSlot,
   *   put: typeof putUploadTarget,
   *   markUploaded: typeof markVideoUploaded,
   *   cancel: typeof cancelVideo,
   *   createKey: function(): (string|null),
   *   onChange: function(MessageVideoDraft): void,
   * }} deps
   */
  constructor(file, deps) {
    this.file = file;
    this.deps = deps;
    this.clientKey = deps.createKey();
    this.encoded = null;
    this.slot = null;
    this.objectsStored = false;
    this.videoId = null;
    /** @type {'preparing'|'uploading'|'ready'|'failed'} */
    this.state = 'preparing';
    this.progress = 0;
    this.errorCode = null;
    /** The API refused a request, so a retry repeats the refusal. */
    this.refusedByServer = false;
    this.removed = false;
    /** A send holds the clip. The composer cannot remove it until the send settles. */
    this.claimed = false;
    /** Runs when the composer leaves the conversation during the send. */
    this.onLeave = null;
    /** A send failed with no server answer, so the server can hold the message. The caption must not change. */
    this.captionLocked = false;
    this.controller = null;
    this.run = null;
  }

  /** @returns {boolean} */
  canRetry() {
    return this.state === 'failed' && !FINAL_CODES.has(this.errorCode) && !this.refusedByServer;
  }

  /** The failure copy. A refusal with no copy of its own and no retry tells the user to pick the clip again. */
  failureText() {
    if (!ERROR_KEYS[this.errorCode] && !this.canRetry()) {
      return t('common.pm_video_error_invalid_state');
    }
    return videoErrorText(this.errorCode);
  }

  /** Start the pipeline, or join the running one. Resolves to the uploaded clip id. */
  start() {
    if (this.state === 'ready') {
      return Promise.resolve(this.videoId);
    }
    if (!this.run) {
      this.run = this.pipeline().finally(() => {
        this.run = null;
      });
    }
    return this.run;
  }

  async pipeline() {
    this.errorCode = null;
    this.refusedByServer = false;
    this.controller = new AbortController();
    const { signal } = this.controller;
    try {
      if (!this.encoded) {
        this.update('preparing', 0);
        this.encoded = await this.deps.encode(this.file, {
          signal,
          onProgress: (fraction) => this.update('preparing', fraction * ENCODE_SHARE),
        });
      }
      this.update('uploading', ENCODE_SHARE);
      await this.upload(signal);
      // The confirmation takes no abort signal, so a remove during it ends here.
      if (this.removed) {
        throw new VideoUploadError('aborted');
      }
      this.videoId = this.slot.id;
      this.update('ready', 1);
      return this.videoId;
    } catch (error) {
      if (!this.removed) {
        this.errorCode = error?.code || 'failed';
        // An API refusal with its own code repeats on every retry, apart from auth, timeout, key conflict and rate limit.
        // An error with no code (a storage PUT, a proxy page) keeps the retry. A retry gets fresh upload URLs.
        this.refusedByServer = error?.code !== 'http' && error?.status >= 400 && error?.status < 500
          && ![401, 408, 409, 429].includes(error.status);
        this.update('failed', this.progress);
      }
      throw error;
    }
  }

  async upload(signal, renewed = false) {
    if (!this.slot || !this.objectsStored) {
      if (this.removed) {
        throw new VideoUploadError('aborted');
      }
      const previous = this.slot;
      this.slot = await this.reserveSlot();
      // A retry with a fresh key leaves the slot of the old key unused.
      if (previous && previous.id !== this.slot.id) {
        this.deps.cancel(previous.id).catch(() => {});
      }
      if (this.removed) {
        throw new VideoUploadError('aborted');
      }
      if (this.slot.upload) {
        await this.putObjects(this.slot.upload, signal);
      }
      this.objectsStored = true;
    }
    if (this.slot.status === 'pending') {
      try {
        await this.deps.markUploaded(this.slot.id);
      } catch (error) {
        // A slot past its confirmation window, or with an object missing, cannot be confirmed. A fresh key reserves a fresh slot.
        if (renewed || ![400, 404, 409].includes(error?.status)) {
          throw error;
        }
        this.cancelSlot();
        this.slot = null;
        this.objectsStored = false;
        this.clientKey = this.deps.createKey();
        await this.upload(signal, true);
      }
    }
  }

  async reserveSlot() {
    const request = () => this.deps.createSlot({
      clientKey: this.clientKey,
      durationMs: this.encoded.durationMs,
      bytes: this.encoded.video.size,
    });
    try {
      return await request();
    } catch (error) {
      // A 409 names a key the server will not reuse. A fresh key reserves a fresh slot.
      if (error?.status !== 409) {
        throw error;
      }
      this.clientKey = this.deps.createKey();
      return request();
    }
  }

  async putObjects(upload, signal) {
    const { video, poster } = this.encoded;
    const total = video.size + poster.size;
    let posterSent = 0;
    const report = (videoSent) => {
      const fraction = total > 0 ? (videoSent + posterSent) / total : 1;
      this.update('uploading', ENCODE_SHARE + fraction * (1 - ENCODE_SHARE));
    };
    await this.deps.put(upload.poster, poster, { signal });
    posterSent = poster.size;
    await this.deps.put(upload.video, video, {
      signal,
      onProgress: (fraction) => report(fraction * video.size),
    });
  }

  update(state, progress) {
    this.state = state;
    this.progress = progress;
    if (!this.removed) {
      this.deps.onChange(this);
    }
  }

  /** A send refused the clip. A final refusal fails the draft, so the composer shows it. */
  sendRefused(code) {
    if (FINAL_CODES.has(code)) {
      this.errorCode = code;
      this.update('failed', this.progress);
    }
  }

  /** Stop the pipeline and delete a reserved slot. A sent clip belongs to its message, so pass false then. */
  remove({ cancelOnServer = true } = {}) {
    this.removed = true;
    this.controller?.abort();
    if (!cancelOnServer) {
      return;
    }
    if (this.run) {
      // A slot request in flight can land after the abort.
      this.run.catch(() => {}).finally(() => this.cancelSlot());
    } else {
      this.cancelSlot();
    }
  }

  cancelSlot() {
    if (this.slot?.id) {
      this.deps.cancel(this.slot.id).catch(() => {});
    }
  }
}

/** The composer UI around one MessageVideoDraft. */
class VideoSendManager {
  constructor() {
    this.draft = null;
    /** @type {Map<string, MessageVideoDraft>} Claimed clips by conversation, for conversations the composer left. */
    this.detached = new Map();
    this.posterUrl = null;
    this.encodeProbe = supportsVideoEncode();
    /** Counts picks, removals and conversation exits, so a pick that waited on the probe can tell it is stale. */
    this.pickSeq = 0;
    this.elements = {
      container: document.getElementById('video_preview_container'),
      poster: document.getElementById('video_preview_poster'),
      placeholder: document.getElementById('video_preview_placeholder'),
      status: document.getElementById('video_status_text'),
      progress: document.getElementById('video_progress'),
      progressBar: document.getElementById('video_progress_bar'),
      retry: document.getElementById('video_retry_btn'),
      remove: document.getElementById('video_remove_btn'),
      message: document.getElementById('message'),
    };
    this.messagePlaceholder = this.elements.message?.getAttribute('placeholder') || '';
    this.bindEvents();
  }

  bindEvents() {
    const { retry, remove } = this.elements;
    retry?.addEventListener('click', () => {
      this.draft?.start().catch(() => {});
    });
    remove?.addEventListener('click', () => this.remove());
  }

  /** @param {File} file a clip from the shared picker */
  async pickFile(file) {
    const seq = ++this.pickSeq;
    const supported = await this.encodeProbe;
    if (seq !== this.pickSeq) {
      return;
    }
    if (!supported) {
      this.showError(t('common.pm_video_not_supported'));
      return;
    }
    // Photos picked during the probe make a mix, and a message carries one attachment.
    if (window.photoUpload?.hasPhoto()) {
      this.showError(t('common.pm_video_one_at_a_time'));
      return;
    }
    this.pick(file);
  }

  /** @param {File} file */
  pick(file) {
    if (this.draft?.claimed) {
      return;
    }
    this.remove();
    // A message carries one attachment, and a reply is one.
    window.cancelReply?.();
    this.draft = new MessageVideoDraft(file, {
      encode: async (blob, options) => (await loadEncoder()).encodeMessageClip(blob, options),
      createSlot: createVideoSlot,
      put: putUploadTarget,
      markUploaded: markVideoUploaded,
      cancel: cancelVideo,
      createKey: createUuid,
      onChange: (draft) => this.render(draft),
    });
    if (!this.draft.clientKey) {
      this.draft = null;
      this.showError(t('common.pm_video_not_supported'));
      return;
    }
    this.show();
    this.draft.start().catch(() => {});
  }

  show() {
    this.elements.container?.classList.remove('hidden');
    this.elements.message?.setAttribute('placeholder', t('common.pm_video_caption_placeholder'));
    this.render(this.draft);
    this.syncComposer();
  }

  /** @param {MessageVideoDraft} draft */
  render(draft) {
    // A phone-gate refusal goes to the verify page once, also for a draft the composer left. The copy shows until it loads.
    if (draft.state === 'failed' && draft.errorCode === PHONE_VERIFICATION_REQUIRED && !draft.gateFollowed) {
      draft.gateFollowed = true;
      followWebPhoneGateRefusal({ error: draft.errorCode });
    }
    if (draft !== this.draft) {
      return;
    }
    const { status, progress, progressBar, retry, poster, placeholder } = this.elements;
    const percent = Math.round(draft.progress * 100);
    if (progressBar) {
      progressBar.style.width = `${percent}%`;
    }
    progress?.setAttribute('aria-valuenow', String(percent));
    progress?.classList.toggle('hidden', draft.state === 'failed' || draft.state === 'ready');

    if (status) {
      status.textContent = this.statusText(draft);
      status.classList.toggle('text-red-600', draft.state === 'failed');
      status.classList.toggle('dark:text-red-400', draft.state === 'failed');
    }
    retry?.classList.toggle('hidden', !draft.canRetry());
    if (this.elements.remove) {
      this.elements.remove.disabled = draft.claimed;
    }

    if (draft.encoded && !this.posterUrl && poster) {
      this.posterUrl = URL.createObjectURL(draft.encoded.poster);
      poster.src = this.posterUrl;
      poster.classList.remove('hidden');
      placeholder?.classList.add('hidden');
    }
  }

  /** @param {MessageVideoDraft} draft */
  statusText(draft) {
    switch (draft.state) {
      case 'preparing':
        return t('common.pm_video_preparing');
      case 'uploading':
        return t('common.pm_video_uploading');
      case 'ready':
        return t('common.pm_video_ready');
      default:
        return draft.failureText();
    }
  }

  /**
   * Remove the clip from the composer. A clip that a send holds stays until the send settles.
   * @param {{cancelOnServer?: boolean}} [options] false after a send, because the message holds the clip
   */
  remove({ cancelOnServer = true } = {}) {
    // A removal also drops a pick that still waits on the probe.
    this.pickSeq += 1;
    const draft = this.draft;
    if (!draft || draft.claimed) {
      return;
    }
    draft.remove({ cancelOnServer });
    this.reset();
  }

  /** The composer leaves the conversation. A clip that a send holds goes on with the send. */
  leave() {
    this.pickSeq += 1;
    const draft = this.draft;
    if (draft?.claimed) {
      this.reset();
      this.detached.set(String(draft.threadId), draft);
      draft.onLeave?.();
    } else if (draft) {
      this.remove();
    } else {
      this.syncComposer();
    }
  }

  /**
   * The composer opens a conversation. A send that still runs for it comes back with its caption,
   * so the user does not send the caption or the clip a second time.
   * @param {string} threadId
   */
  enter(threadId) {
    const draft = this.detached.get(String(threadId));
    if (!this.draft && draft?.claimed) {
      this.detached.delete(String(threadId));
      this.draft = draft;
      if (this.elements.message) {
        this.elements.message.value = draft.caption;
      }
      this.lockCaption(true);
      this.show();
      return;
    }
    this.syncComposer();
  }

  reset() {
    this.draft = null;
    this.lockCaption(false);
    if (this.posterUrl) {
      URL.revokeObjectURL(this.posterUrl);
      this.posterUrl = null;
    }
    const { container, poster, placeholder, progressBar, message } = this.elements;
    container?.classList.add('hidden');
    poster?.classList.add('hidden');
    poster?.removeAttribute('src');
    placeholder?.classList.remove('hidden');
    if (progressBar) {
      progressBar.style.width = '0%';
    }
    message?.setAttribute('placeholder', this.messagePlaceholder);
    this.syncComposer();
  }

  /** Refresh the send button and the attachment options. messages.js decides them. */
  syncComposer() {
    window.updateSendButtonState?.();
  }

  hasVideo() {
    return this.draft !== null;
  }

  isSending() {
    return Boolean(this.draft?.claimed);
  }

  /** @param {boolean} locked */
  lockCaption(locked) {
    if (this.elements.message) {
      this.elements.message.readOnly = locked;
    }
  }

  /**
   * Claim the clip for a send. Waits for the encode and the upload, and retries a failed step.
   * The caption stays read-only and the clip stays in the composer until release().
   * Rejects with composer copy. `attached` on the error is false when the composer left the conversation.
   * @param {{onLeave?: function(): void, threadId?: string|null, caption?: string}} [options] threadId and caption
   *   let a return to the conversation during the send bring the clip back
   * @returns {Promise<{videoId: string, draft: MessageVideoDraft}>}
   */
  async claim({ onLeave, threadId = null, caption = '' } = {}) {
    const draft = this.draft;
    if (!draft) {
      throw new Error(t('common.pm_video_failed'));
    }
    // The running send carries this clip.
    if (draft.claimed) {
      throw Object.assign(new Error(t('common.pm_video_failed')), { attached: false });
    }
    if (draft.state === 'failed' && !draft.canRetry()) {
      throw new Error(draft.failureText());
    }
    draft.claimed = true;
    draft.onLeave = onLeave || null;
    draft.threadId = threadId;
    draft.caption = caption;
    this.lockCaption(true);
    this.render(draft);
    try {
      return { videoId: await draft.start(), draft };
    } catch (error) {
      const attached = this.release(draft);
      const text = draft.state === 'failed' ? draft.failureText() : videoErrorText(error?.code);
      throw Object.assign(new Error(text), { attached });
    }
  }

  /**
   * End a claim. A sent clip leaves the composer. A refused clip shows the refusal.
   * @param {MessageVideoDraft} draft
   * @param {{sent?: boolean, code?: string, answered?: boolean}} [result] answered is false when the send got no server answer
   * @returns {boolean} false when the composer left the conversation during the send
   */
  release(draft, { sent = false, code, answered = true } = {}) {
    draft.claimed = false;
    draft.onLeave = null;
    if (this.detached.get(String(draft.threadId)) === draft) {
      this.detached.delete(String(draft.threadId));
    }
    if (draft !== this.draft) {
      // The composer left. A clip that did not go out is deleted.
      if (!sent) {
        draft.remove();
      }
      return false;
    }
    if (sent) {
      this.remove({ cancelOnServer: false });
      return true;
    }
    // With no server answer, the server can hold the message, and a repeat must carry the same caption.
    draft.captionLocked = draft.captionLocked || !answered;
    // A reply chosen during the send replaces the clip: a message carries one attachment.
    // A clip that the server can hold stays, so the reply goes.
    if (document.getElementById('reply_to_id')?.value) {
      if (!draft.captionLocked) {
        this.remove();
        return true;
      }
      window.cancelReply?.();
    }
    this.lockCaption(draft.captionLocked);
    if (code) {
      draft.sendRefused(code);
    }
    this.render(draft);
    // The send button can stay disabled from a return to this conversation during the send.
    this.syncComposer();
    return true;
  }

  showError(message) {
    window.showError(message);
  }
}

function initVideoSend() {
  const manager = new VideoSendManager();
  window.videoSend = {
    hasVideo: () => manager.hasVideo(),
    pick: (file) => manager.pickFile(file),
    claim: (options) => manager.claim(options),
    release: (draft, result) => manager.release(draft, result),
    remove: (options) => manager.remove(options),
    leave: () => manager.leave(),
    enter: (threadId) => manager.enter(threadId),
    isSending: () => manager.isSending(),
    errorText: videoErrorText,
  };
  manager.syncComposer();
  return manager;
}

if (typeof $ !== 'undefined') {
  $(document).ready(() => {
    if (document.getElementById('video_preview_container')) {
      initVideoSend();
    }
  });
}
